import React, { useState, useEffect } from 'react';
import { Sparkles, Wand2, CreditCard, Loader2, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../utils/api';

const AIDesignerModal = ({ open, onOpenChange, onBlocksGenerated }) => {
  const [prompt, setPrompt] = useState('');
  const [aiProvider, setAiProvider] = useState('openai');
  const [aiModel, setAiModel] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [tone, setTone] = useState('');
  const [generateImages, setGenerateImages] = useState(false);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [models, setModels] = useState({});
  const [packages, setPackages] = useState([]);
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCredits();
      fetchModels();
      fetchPackages();
    }
  }, [open]);

  useEffect(() => {
    // Set default model when provider changes
    if (models[aiProvider]) {
      setAiModel(models[aiProvider].default);
    }
  }, [aiProvider, models]);

  const fetchCredits = async () => {
    try {
      const response = await api.get('/ai/credits');
      setCredits(response.data.credits);
    } catch (error) {
      console.error('Failed to fetch credits', error);
    } finally {
      setLoadingCredits(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await api.get('/ai/models');
      setModels(response.data);
    } catch (error) {
      console.error('Failed to fetch models', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get('/ai/packages');
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Failed to fetch packages', error);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description for your page');
      return;
    }

    if (credits < 1) {
      toast.error('Insufficient credits. Please purchase more credits.');
      setShowBuyCredits(true);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/design', {
        prompt: prompt.trim(),
        ai_provider: aiProvider,
        ai_model: aiModel || undefined,
        business_type: businessType || undefined,
        tone: tone || undefined,
        generate_images: generateImages,
      });

      const { blocks, credits_remaining, images_generated } = response.data;
      setCredits(credits_remaining);

      const imgSuffix = images_generated > 0 ? ` Generated ${images_generated} image${images_generated === 1 ? '' : 's'}.` : '';
      toast.success(`Page design generated!${imgSuffix} ${credits_remaining} credits remaining.`);
      onBlocksGenerated(blocks);
      onOpenChange(false);
      setPrompt('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate design';
      toast.error(message);
      if (error.response?.status === 402) {
        setShowBuyCredits(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCredits = async (packageId) => {
    try {
      const response = await api.post(`/ai/purchase-credits?package=${packageId}`);
      window.open(response.data.url, '_blank');
      toast.success('Redirecting to checkout...');
    } catch (error) {
      toast.error('Failed to initiate purchase');
    }
  };

  const examplePrompts = [
    "Landing page for a coffee shop with hero, features, menu highlights, and contact",
    "Portfolio page with hero introduction, 3-column project gallery, about section",
    "SaaS product page with features grid, pricing comparison, testimonials",
    "Restaurant menu page with food categories, specials, and reservation info",
    "Blog homepage with featured post, recent articles grid, newsletter signup"
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="text-purple-500" size={24} />
            AI Page Designer
          </DialogTitle>
        </DialogHeader>

        {showBuyCredits ? (
          <div className="py-6">
            <div className="text-center mb-6">
              <CreditCard size={48} className="mx-auto text-slate-300 mb-3" />
              <h3 className="text-lg font-semibold">Purchase AI Credits</h3>
              <p className="text-slate-500 text-sm">Each AI design generation uses 1 credit</p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              {packages.map(pkg => (
                <div 
                  key={pkg.id}
                  className="border border-slate-200 rounded-lg p-4 text-center hover:border-purple-400 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handlePurchaseCredits(pkg.id)}
                >
                  <div className="text-2xl font-bold text-purple-600">{pkg.credits}</div>
                  <div className="text-sm text-slate-500 mb-2">credits</div>
                  <div className="text-xl font-semibold">${pkg.price}</div>
                  <div className="text-xs text-slate-400 mt-1">{pkg.description}</div>
                </div>
              ))}
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowBuyCredits(false)}
            >
              Back to Designer
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* Credits Display */}
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Sparkles size={18} className="text-purple-500" />
                  <span className="font-medium">Available Credits:</span>
                  {loadingCredits ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <span className="text-lg font-bold text-purple-600">{credits}</span>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowBuyCredits(true)}
                >
                  <CreditCard size={14} className="mr-1" />
                  Buy Credits
                </Button>
              </div>

              {/* AI Provider Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">AI Provider</label>
                  <Select value={aiProvider} onValueChange={setAiProvider}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI (GPT)</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                      <SelectItem value="gemini">Google (Gemini)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Model</label>
                  <Select value={aiModel} onValueChange={setAiModel}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {models[aiProvider]?.options.map(model => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Business Type + Tone */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Business Type <span className="text-xs text-slate-400">(optional)</span></label>
                  <Select value={businessType || 'none'} onValueChange={(v) => setBusinessType(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="ai-business-type">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Any</SelectItem>
                      <SelectItem value="saas">SaaS / Software</SelectItem>
                      <SelectItem value="agency">Agency / Services</SelectItem>
                      <SelectItem value="ecommerce">E-commerce</SelectItem>
                      <SelectItem value="restaurant">Restaurant / Hospitality</SelectItem>
                      <SelectItem value="portfolio">Portfolio / Creative</SelectItem>
                      <SelectItem value="blog">Blog / Publication</SelectItem>
                      <SelectItem value="nonprofit">Nonprofit / Charity</SelectItem>
                      <SelectItem value="fitness">Fitness / Wellness</SelectItem>
                      <SelectItem value="education">Education / Training</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Tone <span className="text-xs text-slate-400">(optional)</span></label>
                  <Select value={tone || 'none'} onValueChange={(v) => setTone(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="ai-tone">
                      <SelectValue placeholder="Default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Default</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="playful">Playful</SelectItem>
                      <SelectItem value="bold">Bold</SelectItem>
                      <SelectItem value="minimal">Minimal</SelectItem>
                      <SelectItem value="warm">Warm / Friendly</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="luxurious">Luxurious</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Prompt Input */}
              <div>
                <label className="text-sm font-medium mb-1 block">Describe Your Page</label>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="E.g., Create a landing page for a fitness app with hero section, 3 feature columns with icons, testimonial slider, and call-to-action..."
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Generate Images Toggle */}
              <label
                className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                data-testid="ai-generate-images-toggle"
              >
                <input
                  type="checkbox"
                  checked={generateImages}
                  onChange={(e) => setGenerateImages(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-purple-600"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    Generate real images
                    <span className="text-[10px] uppercase tracking-wide bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Nano Banana</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Replace placeholder images with AI-generated photos (saves to your Media Library). Adds ~5-10 seconds per image. Same 1 credit cost.
                  </div>
                </div>
              </label>

              {/* Example Prompts */}
              <div>
                <label className="text-xs text-slate-500 mb-2 block">Example prompts:</label>
                <div className="flex flex-wrap gap-2">
                  {examplePrompts.slice(0, 3).map((example, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPrompt(example)}
                      className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors truncate max-w-[200px]"
                      title={example}
                    >
                      {example.slice(0, 40)}...
                    </button>
                  ))}
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="text-amber-800">
                  <strong>Tip:</strong> The AI now knows your brand colors and fonts and can use Pricing, Testimonials, FAQ, CTA, Team and Stats blocks. Picking a Business Type and Tone above significantly improves output quality.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleGenerate}
                disabled={loading || !prompt.trim() || credits < 1}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 size={16} className="mr-2" />
                    Generate Design (1 credit)
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIDesignerModal;
